// Axios-based API client — TS port of the source (Falah) app's
// `frontend/src/api/client.js`. This is a deliberate architecture change
// from the target's previous hand-rolled `fetch` wrapper: the backend is now
// a Sanctum bearer-token API (not cookie/session), so every request carries
// `Authorization: Bearer <token>` from localStorage instead of a CSRF
// cookie dance.
//
// Every environment serves the Laravel API at `/api` on the same host as
// the SPA (see vite.config.ts's dev proxy and Apache's alias in
// production), so the default base URL below is simply '/api'.
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';

export { API_BASE_URL };

// ---------------------------------------------------------------------------
// Auth user shape — mirrors AuthController::profile() / UserController's
// RESPONSE_FIELDS on the backend exactly. `roles` and `permissions` are
// flat arrays of string names (Spatie role/permission names), not objects —
// this is a deliberate difference from the target's previous cookie-auth
// shape (which nested permissions under role objects).
// ---------------------------------------------------------------------------
export interface AuthUser {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  status: 'active' | 'inactive';
  mobile: string | null;
  address: string | null;
  gender: 'male' | 'female' | null;
  date_of_birth: string | null;
  formatted_id: string;
  photo_url: string | null;
  notify_security_emails: boolean;
  two_factor_enabled: boolean;
  member_id: number | null;
  supplier_id: number | null;
  customer_id: number | null;
  employer_id: number | null;
  nationalities: string[];
  languages: string[];
  identities: { id: number; identity_type: string; number: string; issuer: string | null; expiry_date: string | null }[];
  roles: string[];
  permissions: string[];
  is_member: boolean;
  is_supplier: boolean;
  is_customer: boolean;
  is_employer: boolean;
}

// ---------------------------------------------------------------------------
// Typed error shape. Laravel validation failures come back as
// { message, errors: { field: string[] } } — normalized here into a real
// Error subclass so every caller can `catch (err) { if (err instanceof
// ApiError) ... }` instead of poking at `err.response?.data` (axios's raw
// error shape) with `any`.
// ---------------------------------------------------------------------------
export interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(status: number, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }

  /** First message for a given field, if the backend returned one. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0];
  }
}

// ---------------------------------------------------------------------------
// 401 signalling. The interceptor below clears the stored token and notifies
// every subscriber (AuthContext subscribes once, on mount) rather than doing
// a hard `window.location` redirect — this keeps the SPA's own router
// (RequireAuth) in charge of where the user lands, consistent with how the
// rest of the app navigates.
// ---------------------------------------------------------------------------
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status ?? 0;
    const payload = error.response?.data;
    const message = payload?.message ?? error.message ?? 'Something went wrong.';
    const apiError = new ApiError(status, message, payload?.errors);

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      unauthorizedListeners.forEach((listener) => listener());
    }

    return Promise.reject(apiError);
  }
);

export default api;
